/* eslint-disable no-underscore-dangle */
import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import { z } from 'zod';

import { Text } from '@rover/kibble/core';
import { Form, useKibbleForm, ZodSchema } from '@rover/kibble/forms';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { isStateRequired } from '@rover/react-lib/src/utils/address';
import type { HomeAddressUpdate } from '@rover/rsdk/src/apiClient/latest/model/homeAddressUpdate';

import Container from './Container';
import KibbleAddressFields from './KibbleAddressFields';
import SaveButton from './SaveButton';

export interface KibbleAddressFormProps {
  onSubmit: (data: HomeAddressUpdate) => void;
  defaultValues?: Partial<HomeAddressUpdate> & Record<string, unknown>;
  disableCountry?: boolean;
  submissionErrors?: Record<string, string[]>;
  isLoading?: boolean;
  homeCountryCode?: string;
  extraSchema?: ZodSchema;
  suppressInlineAlerts?: boolean;
  children?: (helpers: {
    configureField: ReturnType<typeof useKibbleForm>['configureField'];
    setValue: ReturnType<typeof useKibbleForm>['setValue'];
    getValues: ReturnType<typeof useKibbleForm>['getValues'];
    watch: ReturnType<typeof useKibbleForm>['watch'];
  }) => React.ReactNode;
}

const KibbleAddressForm: React.FC<KibbleAddressFormProps> = ({
  onSubmit,
  defaultValues,
  disableCountry,
  submissionErrors,
  isLoading,
  homeCountryCode,
  extraSchema,
  suppressInlineAlerts,
  children,
}) => {
  const { locale } = useI18nContext();

  const addressSchema = z
    .object({
      addressLine1: z.string().min(1, t`Please enter a valid address`),
      addressLine2: z.string().optional(),
      city: z.string().min(1, t`Please enter a valid city`),
      state: z.string().optional(),
      zip: z.string().min(1, t`Please enter a valid ZIP code`),
      countryCode: z.string().min(1, t`Please enter a valid country`),
      countryCheckboxes: z.array(z.string()).optional(),
      latitude: z.string().optional(),
      longitude: z.string().optional(),
    })
    .extend(extraSchema ? extraSchema.shape : {})
    .superRefine((data, ctx) => {
      // Check if state is required
      if (isStateRequired(data.countryCode) && (!data.state || data.state.trim() === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t`Please enter a valid state`,
          path: ['state'],
        });
      }
      const baselineCountryCode = homeCountryCode ?? defaultValues?.countryCode;
      const countryChanged = data.countryCode !== baselineCountryCode;
      const isHomeCheckboxSelected = data.isHomeAddress?.includes('home');
      if (
        countryChanged &&
        isHomeCheckboxSelected &&
        !data.countryCheckboxes?.includes('agreeTOS')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t`You must accept the Rover Terms of Service to continue`,
          path: ['countryCheckboxes'],
        });
      }
    });

  const {
    configureField,
    formProps,
    submitButtonProps,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
  } = useKibbleForm({
    schema: addressSchema,
    onValidSubmit: (data) => {
      const submissionData = {
        ...data,
        state: isStateRequired(data.countryCode) ? data.state || '' : '',
        tos: true,
        emailOptIn: data.countryCheckboxes?.includes('emailOptIn') || false,
      };
      onSubmit(submissionData as HomeAddressUpdate);
    },
    defaultValues: {
      latitude: undefined,
      longitude: undefined,
      ...defaultValues,
      countryCheckboxes: [],
    },
  });

  useEffect(() => {
    if (submissionErrors) {
      Object.entries(submissionErrors).forEach(([key, value]) => {
        setError(key as any, { message: value[0] });
      });
    }
  }, [submissionErrors, setError]);

  // Clear state field when switching to a country that doesn't require states
  useEffect(() => {
    const formData = watch((value, { name }) => {
      if (name === 'countryCode' && !isStateRequired(value.countryCode)) {
        setValue('state', '');
      }
    });
    return () => formData.unsubscribe();
  }, [watch, setValue]);

  return (
    <Form {...formProps} hideRequiredFieldWarning>
      <Container>
        {submissionErrors?._All__ && (
          <Text size="100" textColor="error" mb="2x">
            {submissionErrors._All__}
          </Text>
        )}
        <KibbleAddressFields
          addressField={configureField('addressLine1')}
          cityField={configureField('city')}
          stateField={configureField('state')}
          zipField={configureField('zip')}
          countryField={configureField('countryCode')}
          addressLine2Field={configureField('addressLine2')}
          countryCheckboxesField={configureField('countryCheckboxes')}
          showStateField={isStateRequired(getValues('countryCode'))}
          locale={locale}
          setValue={setValue as (name: string, value: string) => void}
          clearErrors={clearErrors as (name?: string) => void}
          disableCountry={disableCountry}
          getValues={getValues}
          suppressInlineAlerts={suppressInlineAlerts}
        />
        {children?.({ configureField, setValue, getValues, watch })}
      </Container>
      <SaveButton
        disabled={submitButtonProps.disabled}
        loading={isLoading}
        label={t`Save`}
        onPress={() => submitButtonProps.onPress({})}
      />
    </Form>
  );
};

export default KibbleAddressForm;
